import { ArrowRight, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  acceptBusinessCenterInvitation,
  getBusinessCenterInvitation,
} from "../../lib/api";
import { getDefaultLandingWorkspaceLabel, resolveAuthenticatedLandingRoute } from "../../lib/defaultLandingWorkspace";

type InvitationSummary = Awaited<ReturnType<typeof getBusinessCenterInvitation>>["invitation"];

export function BusinessCenterInvitationAcceptancePage() {
  const navigate = useNavigate();
  const { token = "" } = useParams();
  const [invitation, setInvitation] = useState<InvitationSummary | null>(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [step, setStep] = useState<"email" | "code" | "complete">("email");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBusinessCenterInvitation(token)
      .then((result) => {
        if (cancelled) return;
        setInvitation(result.invitation);
        setMessage("");
      })
      .catch((error) => {
        if (cancelled) return;
        setInvitation(null);
        setMessage(error instanceof Error ? error.message : "This invitation could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleRequestCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) {
      setMessage("Enter the invited email address before requesting an access code.");
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      const delivery = await acceptBusinessCenterInvitation(token, { email: email.trim(), action: "prepare_login" });
      setDevCode(delivery.devCode || "");
      setStep("code");
      setMessage(delivery.message || "Access code sent. Check your email, then enter the code here.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to send the access code for this invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleComplete(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || !code.trim()) {
      setMessage("Enter the invited email address and one-time access code.");
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      const result = await acceptBusinessCenterInvitation(token, { email: email.trim(), code: code.trim(), action: "complete" });
      const landingRoute = await resolveAuthenticatedLandingRoute({
        email: result.user.email,
        role: result.user.role,
      });
      setStep("complete");
      setMessage("Invitation accepted. Your account is ready.");
      window.setTimeout(() => navigate(landingRoute, { replace: true }), 900);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to complete sign-in for this invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#F5F7FA] px-4 py-10 text-[#1F2937] sm:px-6 lg:px-8">
      <section className="mx-auto grid max-w-3xl gap-6">
        <Link to="/admin/login" className="focus-ring inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 text-sm font-bold text-[#0F766E]">
          Back to admin login
        </Link>

        <article className="overflow-hidden rounded-[2rem] border border-[rgba(20,184,166,.18)] bg-white shadow-[0_24px_80px_rgba(15,23,42,.10)]">
          <header className="border-b border-[rgba(20,184,166,.16)] bg-[#F8FAFC] p-6">
            <div className="inline-flex size-14 items-center justify-center rounded-2xl bg-[#14B8A6] text-white">
              <ShieldCheck className="size-7" aria-hidden="true" />
            </div>
            <p className="mt-4 text-xs font-black uppercase tracking-[0.2em] text-[#14B8A6]">CollabRecover AR Solutions</p>
            <h1 className="mt-2 font-display text-4xl font-bold text-[#0F172A]">Accept your invitation</h1>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">
              Confirm the invited email address, request a one-time access code, and complete secure sign-in.
            </p>
          </header>

          <div className="grid gap-5 p-6">
            {loading ? (
              <p className="rounded-2xl bg-[#F8FAFC] p-4 text-sm font-semibold text-[#475569]">Loading invitation...</p>
            ) : invitation ? (
              <div className="grid gap-3 rounded-3xl border border-[rgba(20,184,166,.18)] bg-[#F8FAFC] p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Invitation details</p>
                <div className="grid gap-2 text-sm font-semibold text-[#334155] sm:grid-cols-2">
                  <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Name</span>{invitation.fullName}</p>
                  <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Role</span>{invitation.role}</p>
                  {invitation.team ? <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Team</span>{invitation.team}</p> : null}
                  {invitation.defaultLandingWorkspace ? <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Default Landing</span>{getDefaultLandingWorkspaceLabel(invitation.defaultLandingWorkspace)}</p> : null}
                  <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Status</span>{invitation.invitationStatus}</p>
                  <p><span className="block text-xs uppercase tracking-[0.14em] text-[#64748B]">Expires</span>{invitation.expiresAt ? new Date(invitation.expiresAt).toLocaleString() : "Not specified"}</p>
                </div>
                {invitation.invitationMessage ? <p className="text-sm font-semibold leading-7 text-[#475569]">{invitation.invitationMessage}</p> : null}
              </div>
            ) : null}

            {step === "email" ? (
              <form onSubmit={handleRequestCode} className="grid gap-4">
                <label className="grid gap-2 text-sm font-extrabold text-[#1F2937]">
                  Invited email address
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    autoComplete="email"
                    className="focus-ring min-h-12 rounded-2xl border border-[rgba(20,184,166,.22)] bg-white px-4 text-sm font-semibold"
                  />
                </label>
                <button
                  type="submit"
                  disabled={!invitation || submitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#14B8A6] px-5 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Mail className="size-4" aria-hidden="true" />
                  {submitting ? "Sending..." : "Send One-Time Access Code"}
                </button>
              </form>
            ) : null}

            {step === "code" ? (
              <form onSubmit={handleComplete} className="grid gap-4">
                <label className="grid gap-2 text-sm font-extrabold text-[#1F2937]">
                  One-time access code
                  <input
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="focus-ring min-h-12 rounded-2xl border border-[rgba(20,184,166,.22)] bg-white px-4 text-sm font-semibold"
                  />
                </label>
                {devCode ? (
                  <p className="rounded-2xl bg-[rgba(20,184,166,.08)] p-3 text-sm font-bold text-[#0F766E]">
                    Preview/dev access code: {devCode}
                  </p>
                ) : null}
                <button
                  type="submit"
                  disabled={submitting}
                  className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#14B8A6] px-5 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Verifying..." : "Accept Invitation"}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </button>
              </form>
            ) : null}

            {step === "complete" ? (
              <div className="rounded-3xl border border-[rgba(20,184,166,.22)] bg-[rgba(20,184,166,.08)] p-5">
                <CheckCircle2 className="size-8 text-[#14B8A6]" aria-hidden="true" />
                <p className="mt-3 font-display text-2xl font-bold text-[#0F172A]">Invitation accepted</p>
                <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">Opening your assigned workspace...</p>
              </div>
            ) : null}

            {message ? <p className="rounded-2xl bg-[#F8FAFC] p-4 text-sm font-semibold text-[#475569]" aria-live="polite">{message}</p> : null}
          </div>
        </article>
      </section>
    </main>
  );
}
